import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  TAIWAN_LANDLINE_EXTENSION_PRESET,
  TAIWAN_LANDLINE_EXTENSION_REGEX,
  TAIWAN_MOBILE_OR_LANDLINE_EXTENSION_PRESET,
  TAIWAN_MOBILE_OR_LANDLINE_EXTENSION_REGEX,
  TAIWAN_MOBILE_PRESET,
  TAIWAN_MOBILE_REGEX,
} from '@/lib/phone-validation'
import type { Question } from '@/types/form'
import { CheckCircle2, XCircle } from 'lucide-react'

type ValidationSettingsProps = {
  question: Question
  onChange: (patch: Partial<Question['settings']>) => void
}

type ValidationPreset = {
  value: string
  label: string
  regex?: string
  hint: string
}

const basePresets: ValidationPreset[] = [
  { value: 'none', label: '不限制格式', hint: '只檢查必填與長度。' },
  {
    value: 'email',
    label: '電子郵件',
    regex: '^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$',
    hint: '例如 name@example.com。',
  },
  {
    value: 'phone',
    label: '電話號碼',
    regex: '^[0-9+()\\-\\s]{6,}$',
    hint: '允許國碼、括號、空白與連字號。',
  },
  {
    value: TAIWAN_MOBILE_PRESET,
    label: '台灣手機',
    regex: TAIWAN_MOBILE_REGEX,
    hint: '例如 0912345678、0912-345-678 或 +886912345678。',
  },
  {
    value: TAIWAN_LANDLINE_EXTENSION_PRESET,
    label: '台灣市話含分機',
    regex: TAIWAN_LANDLINE_EXTENSION_REGEX,
    hint: '例如 02-26589848#123、02-2658-9848 分機 123 或 037-321131。',
  },
  {
    value: TAIWAN_MOBILE_OR_LANDLINE_EXTENSION_PRESET,
    label: '台灣手機或市話分機',
    regex: TAIWAN_MOBILE_OR_LANDLINE_EXTENSION_REGEX,
    hint: '例如 0912345678、+886912345678、02-26589848#123 或 02-26589848 分機 123。',
  },
  {
    value: 'url',
    label: '網站連結',
    regex: '^https?://.+\\..+',
    hint: '必須以 http:// 或 https:// 開頭。',
  },
  {
    value: 'number',
    label: '數字',
    regex: '^-?\\d+(\\.\\d+)?$',
    hint: '允許整數與小數。',
  },
  {
    value: 'integer',
    label: '整數',
    regex: '^-?\\d+$',
    hint: '只允許整數。',
  },
  { value: 'custom', label: '自訂 Regex', hint: '使用下方自訂正則測試。' },
]

export function ValidationSettings({ question, onChange }: ValidationSettingsProps) {
  const presets = getPresets(question.type)
  const presetValue = getPresetValue(question, presets)
  const selectedPreset = presets.find((preset) => preset.value === presetValue) ?? presets[0]
  const regexSource =
    presetValue === 'custom'
      ? String(question.settings.validationRegex ?? '')
      : (selectedPreset.regex ?? '')
  const testValue = String(
    question.settings.validationTestValue ?? getDefaultTestValue(question.type),
  )
  const testResult = testRegex(regexSource, testValue)

  function changePreset(nextPreset: string) {
    const preset = presets.find((item) => item.value === nextPreset)
    onChange({
      validationPreset: nextPreset,
      validation: presetToValidation(nextPreset),
      validationRegex: nextPreset === 'custom' ? question.settings.validationRegex : preset?.regex,
    })
  }

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-slate-200 bg-white p-3 transition-colors hover:border-indigo-200 hover:bg-indigo-50/35">
      <div>
        <h4 className="text-sm font-semibold text-slate-950">驗證與錯誤提示</h4>
        <p className="mt-1 text-xs leading-5 text-slate-500">
          設定格式驗證、常用 preset、自訂 regex 與測試輸入。這些設定目前保存在前端 contract。
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <Label>格式 preset</Label>
        <Select value={presetValue} onValueChange={changePreset}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {presets.map((preset) => (
              <SelectItem key={preset.value} value={preset.value}>
                {preset.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-slate-500">{selectedPreset.hint}</p>
      </div>

      <div className="flex flex-col gap-2">
        <Label>自訂 Regex</Label>
        <Input
          value={String(question.settings.validationRegex ?? selectedPreset.regex ?? '')}
          disabled={presetValue !== 'custom'}
          placeholder="例如：^[A-Z]{2}-\\d{4}$"
          onChange={(event) => onChange({ validationRegex: event.target.value })}
          className="font-mono text-xs"
        />
      </div>

      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-end gap-2">
        <div className="flex flex-col gap-2">
          <Label>測試輸入</Label>
          <Input
            value={testValue}
            onChange={(event) => onChange({ validationTestValue: event.target.value })}
          />
        </div>
        <div
          className={`mb-px inline-flex h-10 items-center gap-1 rounded-md border px-3 text-xs font-medium ${
            testResult.ok
              ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
              : 'border-rose-200 bg-rose-50 text-rose-700'
          }`}
        >
          {testResult.ok ? <CheckCircle2 className="size-4" /> : <XCircle className="size-4" />}
          {testResult.label}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <Label>自訂錯誤文案</Label>
        <Input
          value={String(question.settings.errorMessage ?? '')}
          placeholder="格式不正確，請重新輸入。"
          onChange={(event) => onChange({ errorMessage: event.target.value })}
        />
      </div>
    </div>
  )
}

function getPresets(type: Question['type']) {
  if (type === 'EMAIL') return basePresets.filter((preset) => preset.value === 'email')
  if (type === 'WEBSITE') return basePresets.filter((preset) => preset.value === 'url')
  if (type === 'PHONE_NUMBER') {
    return basePresets.filter((preset) =>
      [
        'phone',
        TAIWAN_MOBILE_PRESET,
        TAIWAN_MOBILE_OR_LANDLINE_EXTENSION_PRESET,
        'custom',
      ].includes(preset.value),
    )
  }
  if (type === 'TAIWAN_MOBILE') {
    return basePresets.filter((preset) => [TAIWAN_MOBILE_PRESET, 'custom'].includes(preset.value))
  }
  if (type === 'TAIWAN_LANDLINE') {
    return basePresets.filter((preset) =>
      ['phone', TAIWAN_LANDLINE_EXTENSION_PRESET, 'custom'].includes(preset.value),
    )
  }
  if (type === 'NUMBER') {
    return basePresets.filter((preset) => ['number', 'integer', 'custom'].includes(preset.value))
  }
  return basePresets
}

function getPresetValue(question: Question, presets: ValidationPreset[]) {
  const stored = String(question.settings.validationPreset ?? question.settings.validation ?? '')
  if (presets.some((preset) => preset.value === stored)) return stored
  if (question.type === 'EMAIL') return 'email'
  if (question.type === 'WEBSITE') return 'url'
  if (question.type === 'TAIWAN_MOBILE') return TAIWAN_MOBILE_PRESET
  if (question.type === 'TAIWAN_LANDLINE') return TAIWAN_LANDLINE_EXTENSION_PRESET
  if (question.type === 'PHONE_NUMBER') return 'phone'
  if (question.type === 'NUMBER') return 'number'
  return 'none'
}

function presetToValidation(value: string): Question['settings']['validation'] {
  if (['email', 'phone', 'url', 'number', 'none'].includes(value)) {
    return value as Question['settings']['validation']
  }
  if (
    value === TAIWAN_MOBILE_PRESET ||
    value === TAIWAN_LANDLINE_EXTENSION_PRESET ||
    value === TAIWAN_MOBILE_OR_LANDLINE_EXTENSION_PRESET
  ) {
    return 'phone'
  }
  if (value === 'integer') return 'number'
  return 'none'
}

function getDefaultTestValue(type: Question['type']) {
  if (type === 'EMAIL') return 'buyer@example.com'
  if (type === 'WEBSITE') return 'https://miao-form.case4.app'
  if (type === 'PHONE_NUMBER') return '+886 912 345 678'
  if (type === 'TAIWAN_MOBILE') return '0912345678'
  if (type === 'TAIWAN_LANDLINE') return '02-2345-6789#123'
  if (type === 'NUMBER') return '1200'
  return '測試內容'
}

function testRegex(regexSource: string, value: string) {
  if (!regexSource) return { ok: true, label: '未限制' }
  try {
    const regex = new RegExp(regexSource)
    return regex.test(value) ? { ok: true, label: '符合' } : { ok: false, label: '不符合' }
  } catch {
    return { ok: false, label: 'Regex 錯誤' }
  }
}
